// src/components/weather/WeatherSourceBadge.jsx
// Weather-source disclosure for a project's runs.
//
// Read-only: the source is *chosen* on the Weather Data page
// (WeatherSourceSelector, rendered by pages/feasibility/FeasibilityWeather).
// This keeps the simulation page an inspector, not a second place to change the
// setting.
//
// Two modes, distinguished on purpose:
//   - No run stamp yet  -> shows the SELECTED source ("will be used"), read
//     from tmy_imports.
//   - Run stamp present -> shows the source the run ACTUALLY used, read from
//     the run summary's `weather_source`. That is the authoritative record,
//     including every substitution the engine applied.
//
// Clicking expands the detail: dataset identity, time base, substitutions and
// any import warnings. Nothing about the weather source should require the user
// to go looking somewhere else.
import { useEffect, useMemo, useState } from "react";
import {
  Chip,
  Divider,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { listProjectTmy } from "../../services/api";

const FORMAT_LABELS = { csv_template: "CSV template", pvgis: "PVGIS", epw: "EPW" };

const TIME_BASE_LABELS = {
  local_standard_time_no_dst:
    "Local standard time, no daylight saving (a typical year has no real calendar)",
};

function DetailLine({ icon, children }) {
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      {icon}
      <Typography variant="caption" color="text.secondary">
        {children}
      </Typography>
    </Stack>
  );
}

export default function WeatherSourceBadge({
  projectId,
  accessToken,
  asset = "pv",
  runWeatherSource = null,
}) {
  const [selected, setSelected] = useState(null); // { imported, name?, format?, validation? }
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!projectId) {
        if (alive) setSelected(null);
        return;
      }
      try {
        const data = await listProjectTmy(projectId, accessToken);
        const flag = asset === "wind" ? "is_active_wind" : "is_active_pv";
        const active = (data?.imports || []).find((r) => r[flag]);
        if (alive) {
          setSelected(
            active
              ? {
                  imported: true,
                  name: active.original_filename || active.id,
                  format: active.source_format,
                  validation: active.validation || {},
                }
              : { imported: false }
          );
        }
      } catch {
        if (alive) setSelected({ imported: false });
      }
    })();
    return () => {
      alive = false;
    };
  }, [projectId, accessToken, asset]);

  // The run stamp wins when present: it describes what happened, not what is
  // configured. They can legitimately differ if the selection changed after
  // the run — which is exactly the case worth surfacing.
  const view = useMemo(() => {
    if (runWeatherSource) {
      const imported = runWeatherSource.kind === "imported_tmy";
      return {
        fromRun: true,
        imported,
        name: runWeatherSource.original_filename || null,
        format: runWeatherSource.source_format || null,
        timeBase: runWeatherSource.time_base || null,
        offsetHours: runWeatherSource.utc_offset_hours,
        elevationSource: runWeatherSource.elevation_source || null,
        rows: runWeatherSource.rows || null,
        substitutions: runWeatherSource.substitutions || [],
        notes: runWeatherSource.import_info || [],
        warnings: runWeatherSource.import_warnings || [],
      };
    }
    if (!selected) return null;
    return {
      fromRun: false,
      imported: Boolean(selected.imported),
      name: selected.name || null,
      format: selected.format || null,
      timeBase: null,
      offsetHours: null,
      elevationSource: null,
      rows: null,
      substitutions: [],
      notes: selected.validation?.info || [],
      warnings: selected.validation?.warnings || [],
    };
  }, [runWeatherSource, selected]);

  if (!view) return null;

  // No "Source:" prefix when describing the next run — the chip sits next to a
  // "Weather source" label already. "Used:" stays on the run case, where the
  // past tense is the whole point: it reports what a finished run consumed.
  const prefix = view.fromRun ? "Used: " : "";
  const label = view.imported
    ? `${prefix}Imported TMY${view.name ? ` · ${view.name}` : ""}`
    : `${prefix}Platform internal`;

  const hasDetail =
    view.imported ||
    view.substitutions.length > 0 ||
    view.notes.length > 0 ||
    view.warnings.length > 0;

  const open = Boolean(anchorEl);

  return (
    <>
      <Chip
        size="small"
        variant={view.imported ? "filled" : "outlined"}
        color={view.imported ? "primary" : "default"}
        clickable={hasDetail}
        onClick={hasDetail ? (e) => setAnchorEl(e.currentTarget) : undefined}
        aria-expanded={open}
        aria-label={`${label}${hasDetail ? " — show details" : ""}`}
        deleteIcon={
          <ExpandMoreIcon
            sx={{
              transition: "transform 150ms",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
            }}
          />
        }
        onDelete={hasDetail ? (e) => setAnchorEl(e.currentTarget) : undefined}
        label={label}
        sx={{ fontWeight: 700, maxWidth: 420 }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { p: 1.5, maxWidth: 460, borderRadius: 2 } } }}
      >
        <Stack spacing={1}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            {view.imported ? "Imported TMY dataset" : "Platform internal weather"}
          </Typography>

          <Typography variant="caption" color="text.secondary">
            {view.fromRun
              ? "This is the weather source the stored result was produced from."
              : "This is the source the next run will use."}
          </Typography>

          {view.imported && (
            <>
              <Divider />
              <Stack spacing={0.5}>
                {view.name && (
                  <DetailLine>
                    File: <b>{view.name}</b>
                    {view.format ? ` (${FORMAT_LABELS[view.format] || view.format})` : ""}
                  </DetailLine>
                )}
                {view.rows ? <DetailLine>{view.rows} hourly steps mapped.</DetailLine> : null}
                {view.timeBase && (
                  <DetailLine>
                    {TIME_BASE_LABELS[view.timeBase] || view.timeBase}
                    {Number.isFinite(view.offsetHours)
                      ? ` · UTC${view.offsetHours >= 0 ? "+" : ""}${view.offsetHours}`
                      : ""}
                  </DetailLine>
                )}
                {view.elevationSource && (
                  <DetailLine>Elevation from {view.elevationSource}.</DetailLine>
                )}
                <DetailLine>
                  Platform weather is not fetched or blended for this asset.
                </DetailLine>
              </Stack>
            </>
          )}

          {view.substitutions.length > 0 && (
            <>
              <Divider />
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                Substituted values used by this run
              </Typography>
              <Stack spacing={0.5}>
                {view.substitutions.map((s, i) => (
                  <DetailLine
                    key={i}
                    icon={
                      <InfoOutlinedIcon
                        sx={{ fontSize: 15, color: "info.main", mt: "2px", flexShrink: 0 }}
                      />
                    }
                  >
                    {s}
                  </DetailLine>
                ))}
              </Stack>
            </>
          )}

          {view.notes.length > 0 && (
            <>
              <Divider />
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                How this dataset is used
              </Typography>
              <Stack spacing={0.5}>
                {view.notes.map((m, i) => (
                  <DetailLine
                    key={i}
                    icon={
                      <InfoOutlinedIcon
                        sx={{ fontSize: 15, color: "info.main", mt: "2px", flexShrink: 0 }}
                      />
                    }
                  >
                    {m}
                  </DetailLine>
                ))}
              </Stack>
            </>
          )}

          {view.warnings.length > 0 && (
            <>
              <Divider />
              <Stack spacing={0.5}>
                {view.warnings.map((w, i) => (
                  <DetailLine
                    key={i}
                    icon={
                      <WarningAmberOutlinedIcon
                        sx={{ fontSize: 15, color: "warning.main", mt: "2px", flexShrink: 0 }}
                      />
                    }
                  >
                    {w}
                  </DetailLine>
                ))}
              </Stack>
            </>
          )}

          <Divider />
          <Typography variant="caption" color="text.secondary">
            Change the source on the Weather Data page.
          </Typography>
        </Stack>
      </Popover>
    </>
  );
}
